import { CHART_COLORS } from './../common/color-util';
import { Subscription } from 'rxjs';
import { AppConfig } from './../app.config';
import { ExplainTablesComponent } from './explain-tables.component';
import { ExplainData } from '@liquitics/transaction';
import { AmChartsService } from '@amcharts/amcharts3-angular';
import { ExplainService } from './explain.service';
import { DashboardEventService } from '../dashboard/dashboard-event.service';
import { Component, OnInit, Input, ViewChild, OnDestroy } from '@angular/core';
import { SnapshotService } from './../snapshot/snapshot.service';
import {
    trigger,
    state,
    style,
    animate,
    transition
} from '@angular/animations';
import { take } from 'rxjs/operators';
import * as moment from 'moment';

// am4
// import * as am4core from "@amcharts/amcharts4/core";
// import * as am4charts from "@amcharts/amcharts4/charts";
// import am4themes_animated from "@amcharts/amcharts4/themes/animated";
// am4core.useTheme(am4themes_animated);
// import * as AmChartsLogo from "@amcharts/amcharts4/.internal/core/elements/AmChartsLogo";
// AmChartsLogo.AmChartsLogo['_properties'].visible = false;

@Component({
    selector: 'app-explain',
    templateUrl: './explain.component.html',
    styleUrls: ['./explain.component.scss'],
    animations: [
        trigger('flyInOut', [
            state('in', style({ opacity: 1, transform: 'translateX(0)' })),
            transition('void => *', [
                style({
                    opacity: 0,
                    transform: 'translateX(100%)'
                }),
                animate('0.2s ease-in')
            ]),
            transition('* => void', [
                style({
                    opacity: 0,
                    transform: 'translateX(-100%)'
                })
            ])
        ])
    ]
})
export class ExplainComponent implements OnInit, OnDestroy {
    @Input()
    explainMode = 'BY_SOURCE';
    pulledOut = false;
    byProductType = true;
    byClientGroup = false;

    @ViewChild(ExplainTablesComponent, { static: true }) explainTablesComponent: ExplainTablesComponent;

    liquidityMode = 'gross';
    chart: any;
    // am4
    // chart: am4charts.PieChart;

    panelBlocked = false;

    explainedCurrency: string;
    explainedDate: string;
    isSpot: boolean;
    explainedRequestTime: string;

    subscription: Subscription;
    private profileType: string;

    constructor(protected explainService: ExplainService,
        protected dashboardEventService: DashboardEventService,
        protected snapshotService: SnapshotService,
        protected amChartsService: AmChartsService,
        protected appConfig: AppConfig) {
        this.profileType = this.appConfig.activeUserProfile.getProfileType();
    }



    ngOnInit() {
        this.subscription = this.dashboardEventService.ladderRowSelectionSubject.subscribe({
            next: selection => {
                this.explain(selection.selectedCurrency, selection.currencyIndex, selection.data.bucketStart);
            }
        });
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    getSpotDate(): string {
        return this.appConfig.getTodayStartUTC().add(2, 'days').format('YYYY-MM-DD');
    }

    explain(currency: string, currencyIndex: number, date: string) {
        this.panelBlocked = true;
        this.explainedCurrency = currency;
        this.isSpot = date <= this.getSpotDate();
        this.explainedDate = moment(date).format('L');
        this.explainedRequestTime = moment().format('LLL');
        this.explainTablesComponent.selectedCurrencyIndex = currencyIndex;
        this.explainService.getExplainData(currency, date, date)
            .pipe(take(1))
            .subscribe({
                next: (data: ExplainData) => {
                    if (this.explainMode === 'BY_SOURCE') {
                        this.showExplain(data, true, false, false);
                    } else if (this.explainMode === 'MULTI_ENTITY_NOSTRO') {
                        this.showExplain(data, false, this.byProductType, this.byClientGroup);
                    }
                }, error: (err) => {
                    this.panelBlocked = false;
                }, complete: () => {
                    this.panelBlocked = false;
                }
            })
            ;
    }

    setByProductType() {
        this.byProductType = true;
        this.byClientGroup = false;
    }
    setByClientGroup() {
        this.byProductType = false;
        this.byClientGroup = true;
    }


    private createInitialTotals(): any {
        return this.updateTotals({}, 'REC', 0);
    }

    private updateTotals(runningTotals: any, direction: string, amount: number): any {
        let inAmount = runningTotals['in'] || 0;
        let outAmount = runningTotals['out'] || 0;
        let grossAmount = runningTotals['gross'] || 0;
        let netAmount = runningTotals['net'] || 0;
        if (direction === 'PAY') {
            outAmount += amount;
        } else if (direction === 'REC') {
            inAmount += amount;
        } else {
            console.log('Received invalid direction: ' + direction);
        }
        grossAmount += Math.abs(amount);
        netAmount += amount;
        runningTotals['in'] = inAmount;
        runningTotals['out'] = outAmount;
        runningTotals['gross'] = grossAmount;
        runningTotals['net'] = netAmount;
        return runningTotals;
    }

    private pushChartData(chartData: any[],
        bySource: boolean,
        byProductType: boolean,
        byClientGroup: boolean,
        sourceName: string,
        compactSourceName: string,
        tableData: any[],
        topN: any[],
        originExtractor: (data) => string,
        amountExtractor: (data) => number,
    ) {
        let sources: string[];
        let compactSources: string[];
        let groupedDataBySources: any[];
        if (bySource) {
            groupedDataBySources = [tableData];
            sources = [sourceName];
            compactSources = [compactSourceName];
        } else {
            let keyName;
            if (byProductType) {
                keyName = 'productType';
            } else {
                keyName = 'clientGroup';
            }
            const groupedByKey = tableData.reduce((grouped, data) => {
                const key = data[keyName];
                grouped[key] = grouped[key] || [];
                grouped[key].push(data);
                return grouped;
            }, {});
            sources = Object.keys(groupedByKey);
            compactSources = Object.keys(groupedByKey);
            groupedDataBySources = sources.map((key) => {
                return groupedByKey[key];
            })
        }
        groupedDataBySources.forEach((groupedDataBySource, index) => {
            const groupedTotals = groupedDataBySource.reduce((runningTotals, d) => {
                this.updateTotals(runningTotals, d.direction, amountExtractor(d));
                topN.push({
                    'Source': sources[index],
                    'Origin': originExtractor(d),
                    'Amount': amountExtractor(d),
                    'direction': d.direction,
                });
                return runningTotals;
            }, this.createInitialTotals());
            chartData.push(this.createChartData(sources[index], compactSources[index], groupedTotals));
        })
    }

    showExplain(explainData: ExplainData, bySource?: boolean, byProductType?: boolean, byClientGroup?: boolean) {
        let chartData = [];
        const chartDataBy = [];
        const topN = [];
        this.explainService.emptyForUndefined(explainData);

        // --------------------------------------------------------------------------- //
        if (this.isSpot) {
            this.explainTablesComponent.contractualFlowTableData = explainData.contractualFlowData;
        } else {
            this.explainTablesComponent.contractualFlowTableData = explainData.contractualFlowData.concat(explainData.unexpectedFlowData);
        }

        this.pushChartData(chartData,
            bySource,
            byProductType,
            byClientGroup,
            'Contractual',
            'Contractual',
            this.explainTablesComponent.contractualFlowTableData,
            topN,
            (data) => { return (data.productType + '(' + data.desk + ')') },
            (data) => { return data.flowAmount },
        );
        // let sources: string[];
        // let groupedDataBySources: any[];
        // if (bySource) {
        //     groupedDataBySources = [this.explainTablesComponent.contractualFlowTableData];
        //     sources = ['Contractual'];
        // } else if (byProductType) {
        //     const groupedByProductType = this.explainTablesComponent.contractualFlowTableData.reduce((grouped, data) => {
        //         const key = data.productType;
        //         grouped[key] = grouped[key] || [];
        //         grouped[key].push(data);
        //         return grouped;
        //     }, {});
        //     sources = Object.keys(groupedByProductType);
        //     groupedDataBySources = sources.map((key) => {
        //         return groupedByProductType[key];
        //     })
        // }
        // groupedDataBySources.forEach((groupedDataBySource, index) => {
        //     const groupedTotals = groupedDataBySource.reduce((runningTotals, d) => {
        //         this.updateTotals(runningTotals, d.direction, d.flowAmount);
        //         topN.push({
        //             'Source': sources[index],
        //             'Origin': (d.productType + '(' + d.desk + ')'),
        //             'Amount': d.flowAmount,
        //             'direction': d.direction,
        //         });
        //         return runningTotals;
        //     }, this.createInitialTotals());
        //     chartData.push(this.createChartData(sources[index], 'Contractual', groupedTotals));
        // })

        // --------------------------------------------------------------------------- //
        this.explainTablesComponent.interdeskFundingTableData = explainData.interdeskFundingData;
        // let source = 'Interdesk';
        // let totals = this.explainTablesComponent.interdeskFundingTableData.reduce((runningTotals, d) => {
        //     this.updateTotals(runningTotals, d.direction, d.amount);
        //     topN.push({
        //         'Source': source,
        //         'Origin': d.desk,
        //         'Amount': d.amount,
        //         'direction': d.direction,
        //     });
        //     return runningTotals;
        // }, this.createInitialTotals());
        // chartData.push(this.createChartData(source, 'Interdesk', totals));
        this.pushChartData(chartData,
            bySource,
            byProductType,
            byClientGroup,
            'Interdesk',
            'Interdesk',
            this.explainTablesComponent.interdeskFundingTableData,
            topN,
            (data) => { return data.desk },
            (data) => { return data.amount },
        );
        // --------------------------------------------------------------------------- //
        this.explainTablesComponent.clientLevelFundingTableData = explainData.clientLevelFundingData;
        // source = 'Client Level';
        // totals = this.explainTablesComponent.clientLevelFundingTableData.reduce((runningTotals, d) => {
        //     this.updateTotals(runningTotals, d.direction, d.amount);
        //     topN.push({
        //         'Source': source,
        //         'Origin': d.clientGroup,
        //         'Amount': d.amount,
        //         'direction': d.direction,
        //     });
        //     return runningTotals;
        // }, this.createInitialTotals());
        // chartData.push(this.createChartData(source, 'Client', totals));
        this.pushChartData(chartData,
            bySource,
            byProductType,
            byClientGroup,
            'Client Level',
            'Client',
            this.explainTablesComponent.clientLevelFundingTableData,
            topN,
            (data) => { return data.desk },
            (data) => { return data.amount },
        );
        // --------------------------------------------------------------------------- //
        if (this.isSpot) {
            this.explainTablesComponent.unexpectedFlowTableData = explainData.unexpectedFlowData;
        } else {
            this.explainTablesComponent.unexpectedFlowTableData = [];
        }
        // source = 'Unexpected';
        // totals = this.explainTablesComponent.unexpectedFlowTableData.reduce((runningTotals, d) => {
        //     this.updateTotals(runningTotals, d.direction, d.flowAmount);
        //     topN.push({
        //         'Source': source,
        //         'Origin': (d.productType + '(' + d.desk + ')'),
        //         'Amount': d.flowAmount,
        //         'direction': d.direction,
        //     });
        //     return runningTotals;
        // }, this.createInitialTotals());
        // chartData.push(this.createChartData(source, 'Unexpected', totals));
        this.pushChartData(chartData,
            bySource,
            byProductType,
            byClientGroup,
            'Unexpected',
            'Unexpected',
            this.explainTablesComponent.unexpectedFlowTableData,
            topN,
            (data) => { return (data.productType + '(' + data.desk + ')') },
            (data) => { return data.flowAmount },
        );
        // --------------------------------------------------------------------------- //
        this.explainTablesComponent.predictionTableData = explainData.predictionData;
        // source = 'Predictions';
        // totals = this.explainTablesComponent.predictionTableData.reduce((runningTotals, d) => {
        //     this.updateTotals(runningTotals, d.direction, d.amount);
        //     topN.push({
        //         'Source': source,
        //         'Origin': d.clientGroup,
        //         'Amount': d.amount,
        //         'direction': d.direction,
        //     });
        //     return runningTotals;
        // }, this.createInitialTotals());
        // chartData.push(this.createChartData(source, 'Predictions', totals));
        this.pushChartData(chartData,
            bySource,
            byProductType,
            byClientGroup,
            'Predictions',
            'Predictions',
            this.explainTablesComponent.predictionTableData,
            topN,
            (data) => { return data.clientGroup; },
            (data) => { return data.amount },
        );
        // --------------------------------------------------------------------------- //
        this.explainTablesComponent.correctionTableData = explainData.correctionData;
        // source = 'Corrections';
        // totals = this.explainTablesComponent.correctionTableData.reduce((runningTotals, d) => {
        //     this.updateTotals(runningTotals, d.direction, d.flowAmount);
        //     topN.push({
        //         'Source': source,
        //         'Origin': d.reason,
        //         'Amount': d.flowAmount,
        //         'direction': d.direction,
        //     });
        //     return runningTotals;
        // }, this.createInitialTotals());
        // chartData.push(this.createChartData(source, 'Corrections', totals));
        this.pushChartData(chartData,
            bySource,
            byProductType,
            byClientGroup,
            'Corrections',
            'Corrections',
            this.explainTablesComponent.correctionTableData,
            topN,
            (data) => { return data.reason; },
            (data) => { return data.flowAmount; },
        );
        // --------------------------------------------------------------------------- //
        this.explainTablesComponent.topNDataByType = topN.reduce((grouped, data) => {
            const key = data.Source;
            grouped[key] = grouped[key] || [];
            grouped[key].push(data);

            return grouped;
        }, {})
            ;
        Object.keys(this.explainTablesComponent.topNDataByType)
            .forEach((type: string) => {
                this.explainTablesComponent.topNDataByType[type] = this.explainTablesComponent.topNDataByType[type]
                    .sort((a, b) => {
                        return Math.abs(b['Amount']) - Math.abs(a['Amount']);
                    })
                    .splice(0, 15);
            });

        this.explainTablesComponent.topNData = topN
            .sort((a, b) => {
                return Math.abs(b['Amount']) - Math.abs(a['Amount']);
            })
            .splice(0, 15);
        this.updateChartDataWithLiquidityMode(chartData);


        chartData = this.reduceChartData(chartData);
        this.createExplainChart(chartData);
        this.explainTablesComponent.show(this.liquidityMode, 'top');
    }

    private createChartData(source: string, compactSource: string, totals: any) {
        return {
            'Source': source,
            'CompactSource': compactSource,
            'NetAmount': (totals['net']),
            'GrossAmount': (totals['gross']),
            'InAmount': (totals['in']),
            'OutAmount': (totals['out']),
        };
    }

    private reduceChartData(chartData: any[]): any[] {
        const groupedBySource = chartData.reduce((grouped, data) => {
            const key = data.Source;
            grouped[key] = grouped[key] || [];
            grouped[key].push(data);
            return grouped;
        }, {});
        return Object.keys(groupedBySource)
            .map((key: string) => {
                return groupedBySource[key].reduce((prev, curr) => {
                    prev.NetAmount += curr.NetAmount;
                    prev.GrossAmount += curr.GrossAmount;
                    prev.InAmount += curr.InAmount;
                    prev.OutAmount += curr.OutAmount;
                    return prev;
                })
            })
    }

    onLiquidityModeChange(event) {
        this.liquidityMode = event.value;
        this.amChartsService.updateChart(this.chart, () => {
            if (this.chart && this.chart.dataProvider) {
                this.updateChartDataWithLiquidityMode(this.chart.dataProvider);
                this.chart.validateData();
            }
        });
        this.explainTablesComponent.show(this.liquidityMode, this.explainTablesComponent.selectedDataType);
    }

    private updateChartDataWithLiquidityMode(chartData: any[]) {
        if (chartData) {
            let colToUse;
            if (this.liquidityMode === 'gross') {
                colToUse = 'GrossAmount';
            } else if (this.liquidityMode === 'in') {
                colToUse = 'InAmount';
            } else if (this.liquidityMode === 'out') {
                colToUse = 'OutAmount';
            } else if (this.liquidityMode === 'net') {
                colToUse = 'NetAmount';
            }
            chartData.forEach(element => {
                // Value needs to be positive for pie chart to display
                element['Amount'] = Math.abs(element[colToUse]);
                element['RawAmount'] = element[colToUse];
            });
            this.explainTablesComponent.show(this.liquidityMode, this.explainTablesComponent.selectedDataType);
        }
    }

    protected chartDivName() {
        return 'explainchartdiv';
    }

    // am4
    // private pullOutSingleSlice(target) {
    //     let series = target.dataItem.component as am4charts.PieSeries;
    //     if (target.isActive) {
    //         series.slices.each((item) => {
    //             if (item.isActive && item != target) {
    //                 item.isActive = false;
    //             }
    //         })
    //     }
    // }

    // private drillIn(dataItem) {
    //     dataItem.dataContext['Source']
    //     if (this.profileType !== 'MULTI_ENTITY_NOSTRO' && dataItem.dataContext['Source'] !== undefined) {
    //         const pieWedgeColor = dataItem.slice.fill.toString();
    //         this.explainTablesComponent.headerColor = pieWedgeColor || '';
    //         this.explainTablesComponent.selectedDataType = dataItem.dataContext['Source'];
    //     } else {
    //         this.explainTablesComponent.show(this.liquidityMode, 'topByType', dataItem.dataContext['Source']);
    //         this.pulledOut = true;
    //     }

    // }

    // private drillOut() {
    //     this.explainTablesComponent.headerColor = '';
    //     this.explainTablesComponent.show(this.liquidityMode, 'top');
    //     this.pulledOut = false;
    // }

    private createExplainChart(data: any[]) {
        if (this.chart) {
            this.amChartsService.destroyChart(this.chart);
        }
        this.chart = this.amChartsService.makeChart(this.chartDivName(), {
            'type': 'pie',
            'hideCredits': true,
            'startDuration': 0,
            'theme': 'light',
            'colors': CHART_COLORS,
            'pullOutEffect': 'elastic',
            'pullOutOnlyOne': true,
            'addClassNames': true,
            'classNamePrefix': 'explain',
            'classNameField': 'CompactSource',
            'labelsEnabled': false,
            'legend': {
                'position': 'bottom',
                'marginRight': 10,
                'autoMargins': false,
                'marginTop': 0,
                'switchable': false,
                'markerType': 'circle',
                'align': 'center',
                // 'marginBottom': 40,
                // 'marginLeft': 40,
                // 'valueFunction': (item) => {
                //     return item.dataContext['percents'] + '% (' + item.dataContext['RawAmount'] + ')';
                // },
                // 'labelText': '',
                // 'valueText': '[[percents]]% ([[RawAmount]])',
                'maxColumns': 2,
                'divId': 'explainlegenddiv'
            },
            // 'innerRadius': '30%',
            'defs': {
                'filter': [{
                    'id': 'shadow',
                    'width': '200%',
                    'height': '200%',
                    'feOffset': {
                        'result': 'offOut',
                        'in': 'SourceAlpha',
                        'dx': 0,
                        'dy': 0
                    },
                    'feGaussianBlur': {
                        'result': 'blurOut',
                        'in': 'offOut',
                        'stdDeviation': 5
                    },
                    'feBlend': {
                        'in': 'SourceGraphic',
                        'in2': 'blurOut',
                        'mode': 'normal'
                    }
                }]
            },
            'dataProvider': data,
            'valueField': 'Amount',
            'titleField': 'Source',
            // 'labelText': '[[CompactSource]] [[percents]]% ([[RawAmount]])',
            'showZeroSlices': false,
            'precision': 2,
            'autoMargins': false,
            // 'marginTop': 20,
            // 'marginBottom': 35,
            // 'marginLeft': 5,
            // 'marginRight': 5,
            'pullOutRadius': 10,
            'responsive': {
                'enabled': true,
                'rules': [
                    {
                        'minWidth': 250,
                        'maxWidth': 499,
                        'overrides': {
                            'fontSize': 8,
                            'legend': {
                                'fontSize': 8,
                            }
                        }
                    },
                    {
                        'minWidth': 500,
                        'maxWidth': 599,
                        'overrides': {
                            'fontSize': 12,
                            'legend': {
                                'fontSize': 12,
                            }
                        }
                    },
                    {
                        'minWidth': 600,
                        'overrides': {
                            'fontSize': 14,
                            'legend': {
                                'fontSize': 14,
                            }
                        }
                    }
                ],
            },
            'listeners': [
                {
                    'event': 'pullOutSlice',
                    'method': (event) => {
                        if (this.profileType !== 'MULTI_ENTITY_NOSTRO' && event.dataItem.dataContext['Source'] !== undefined) {
                            const pieWedgeColor = event.dataItem.color || '';
                            this.explainTablesComponent.headerColor = pieWedgeColor;
                            this.explainTablesComponent.selectedDataType = event.dataItem.dataContext['Source'];
                        } else {
                            this.explainTablesComponent.show(this.liquidityMode, 'topByType', event.dataItem.dataContext['Source']);
                            this.pulledOut = true;
                        }
                    }
                },
                {
                    'event': 'pullInSlice',
                    'method': (event) => {
                        this.explainTablesComponent.headerColor = '';
                        this.explainTablesComponent.show(this.liquidityMode, 'top');
                        this.pulledOut = false;
                    }

                },
                {
                    'event': 'rollOverSlice',
                    'method': (event) => {
                        const wedge = event.dataItem.wedge.node;
                        wedge.parentNode.appendChild(wedge);
                    }
                }
            ],
        });

        // ====am4 -- Start

        // if (this.chart) {
        //     this.chart.dispose();
        // }
        // // this.chart = this.amChartsService.makeChart(this.chartDivName(), {
        // this.chart = am4core.create(this.chartDivName(), am4charts.PieChart);
        // this.chart.logo.visible = false;
        // this.chart.responsive.enabled = true;
        // this.chart.data = data;
        // let pieSeries = this.chart.series.push(new am4charts.PieSeries());
        // pieSeries.colors.list = CHART_COLORS
        //     .map((color) => {
        //         return am4core.color(color);
        //     });
        // pieSeries.dataFields.value = 'Amount';
        // pieSeries.dataFields.category = 'Source';
        // pieSeries.events.on('datavalidated', function (ev) {
        //     ev.target.slices.each(function (slice) {
        //         if (slice.dataItem.values.value.percent < 0.1) {
        //             slice.dataItem.hide();
        //         }
        //     });
        // });
        // // const handleOnHit = (target) => {
        // //     let series = target.dataItem.component as am4charts.PieSeries;
        // //     if (target.isActive) {
        // //         const dataItem = target.dataItem;
        // //         series.slices.each((item) => {
        // //             if (item.isActive && item != target) {
        // //                 item.isActive = false;
        // //             }
        // //             if (this.profileType !== 'MULTI_ENTITY_NOSTRO' && dataItem.dataContext['Source'] !== undefined) {
        // //                 const pieWedgeColor = item.fill.toString();
        // //                 this.explainTablesComponent.headerColor = pieWedgeColor || '';
        // //                 this.explainTablesComponent.selectedDataType = dataItem.dataContext['Source'];
        // //             } else {
        // //                 this.explainTablesComponent.show(this.liquidityMode, 'topByType', dataItem.dataContext['Source']);
        // //                 this.pulledOut = true;
        // //             }
        // //         })
        // //     } else {
        // //         this.explainTablesComponent.headerColor = '';
        // //         this.explainTablesComponent.show(this.liquidityMode, 'top');
        // //         this.pulledOut = false;
        // //     }
        // // }
        // pieSeries.slices.template.events.on('hit', (ev) => {
        //     const target = ev.target;
        //     if (target.isActive) {
        //         this.pullOutSingleSlice(target);
        //         this.drillIn(target.dataItem);
        //     } else {
        //         this.drillOut();
        //     }
        // });
        // const legend = new am4charts.Legend();
        // legend.togglable = false;
        // legend.focusable = false;
        // let marker: any = legend.markers.template.children.getIndex(0);
        // marker.cornerRadius(12, 12, 12, 12);
        // legend.markers.template.layout = 'grid';
        // legend.maxColumns = 2;
        // legend.itemContainers.template.clickable = false;
        // legend.markers.template.interactionsEnabled = true;
        // legend.markers.template.events.on('hit', e => {
        //     const target = e.target;
        //     target.isActive = true;
        //     target.show();
        //     const pieSeriesDataItem = target.dataItem.dataContext as am4charts.PieSeriesDataItem;
        //     const dataItem = pieSeriesDataItem.slice.dataItem;
        //     this.drillIn(dataItem);
        // });
        // this.chart.legend = legend;
        // {
        //     'type': 'pie',
        //     'hideCredits': true,
        //     'startDuration': 0,
        //     'theme': 'light',
        //     'colors': CHART_COLORS,
        //     'pullOutEffect': 'elastic',
        //     'pullOutOnlyOne': true,
        //     'addClassNames': true,
        //     'classNamePrefix': 'explain',
        //     'classNameField': 'CompactSource',
        //     'labelsEnabled': false,
        //     'legend': {
        //         'position': 'bottom',
        //         'marginRight': 10,
        //         'autoMargins': false,
        //         'marginTop': 0,
        //         'switchable': false,
        //         'markerType': 'circle',
        //         'align': 'center',
        //         // 'marginBottom': 40,
        //         // 'marginLeft': 40,
        //         // 'valueFunction': (item) => {
        //         //     return item.dataContext['percents'] + '% (' + item.dataContext['RawAmount'] + ')';
        //         // },
        //         // 'labelText': '',
        //         // 'valueText': '[[percents]]% ([[RawAmount]])',
        //         'maxColumns': 2,
        //         'divId': 'explainlegenddiv'
        //     },
        //     // 'innerRadius': '30%',
        //     'defs': {
        //         'filter': [{
        //             'id': 'shadow',
        //             'width': '200%',
        //             'height': '200%',
        //             'feOffset': {
        //                 'result': 'offOut',
        //                 'in': 'SourceAlpha',
        //                 'dx': 0,
        //                 'dy': 0
        //             },
        //             'feGaussianBlur': {
        //                 'result': 'blurOut',
        //                 'in': 'offOut',
        //                 'stdDeviation': 5
        //             },
        //             'feBlend': {
        //                 'in': 'SourceGraphic',
        //                 'in2': 'blurOut',
        //                 'mode': 'normal'
        //             }
        //         }]
        //     },
        //     'dataProvider': data,
        //     'valueField': 'Amount',
        //     'titleField': 'Source',
        //     // 'labelText': '[[CompactSource]] [[percents]]% ([[RawAmount]])',
        //     'showZeroSlices': false,
        //     'precision': 2,
        //     'autoMargins': false,
        //     // 'marginTop': 20,
        //     // 'marginBottom': 35,
        //     // 'marginLeft': 5,
        //     // 'marginRight': 5,
        //     'pullOutRadius': 10,
        //     'responsive': {
        //         'enabled': true,
        //         'rules': [
        //             {
        //                 'minWidth': 250,
        //                 'maxWidth': 499,
        //                 'overrides': {
        //                     'fontSize': 8,
        //                     'legend': {
        //                         'fontSize': 8,
        //                     }
        //                 }
        //             },
        //             {
        //                 'minWidth': 500,
        //                 'maxWidth': 599,
        //                 'overrides': {
        //                     'fontSize': 12,
        //                     'legend': {
        //                         'fontSize': 12,
        //                     }
        //                 }
        //             },
        //             {
        //                 'minWidth': 600,
        //                 'overrides': {
        //                     'fontSize': 14,
        //                     'legend': {
        //                         'fontSize': 14,
        //                     }
        //                 }
        //             }
        //         ],
        //     },
        //     'listeners': [
        //         {
        //             'event': 'pullOutSlice',
        //             'method': (event) => {
        //                 if (this.profileType !== 'MULTI_ENTITY_NOSTRO' && event.dataItem.dataContext['Source'] !== undefined) {
        //                     const pieWedgeColor = event.dataItem.color || '';
        //                     this.explainTablesComponent.headerColor = pieWedgeColor;
        //                     this.explainTablesComponent.selectedDataType = event.dataItem.dataContext['Source'];
        //                 } else {
        //                     this.explainTablesComponent.show(this.liquidityMode, 'topByType', event.dataItem.dataContext['Source']);
        //                     this.pulledOut = true;
        //                 }
        //             }
        //         },
        //         {
        //             'event': 'pullInSlice',
        //             'method': (event) => {
        //                 this.explainTablesComponent.headerColor = '';
        //                 this.explainTablesComponent.show(this.liquidityMode, 'top');
        //                 this.pulledOut = false;
        //             }
        //         },
        //         {
        //             'event': 'rollOverSlice',
        //             'method': (event) => {
        //                 const wedge = event.dataItem.wedge.node;
        //                 wedge.parentNode.appendChild(wedge);
        //             }
        //         }
        //     ],
        // });
        // ====am4 -- End
    }
}
